import React, { useEffect, useState } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import TextArea from 'components/atoms/textarea';
import DraggableModal from 'components/organisms/draggableModal';
import { showResult } from 'infrastructure/hooks/utils';
import { useTracker } from 'infrastructure/hooks/shared/useTracker';
import { getTrackerTitle } from 'infrastructure/hooks/shared/useTracker/getTrackerTitle';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import s from './styles.module.scss';
import { ContactTypeSelect } from './contactType';

import type { ContactType } from './contactType';
import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';
import type { ISmsTemplate } from 'infrastructure/interfaces/i-sms-template';

export interface SmsModalProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  smsTemplates: ISmsTemplate[];
  loading: boolean;
  submit: (
    data: ISendSmsDataProps,
  ) => Promise<{ error: null | 'close alert' | (string & {}) }>;
  isRpmPanel: boolean;
}

const SmsModal: React.FC<SmsModalProps> = ({
  isOpen,
  toggle,
  patientGuid,
  smsTemplates,
  loading,
  submit,
  isRpmPanel,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState<ContactType>('primary');

  const {
    time,
    secondsRef,
    startTracker,
    stopTracker,
    createTrackerLoading,
    trackerGuid,
    deleteTracker,
    resumeTracker,
  } = useTracker();

  useEffect(() => {
    if (isRpmPanel && isOpen) {
      startTracker();
    }
  }, [isRpmPanel, isOpen]);

  const handleSubmit = async () => {
    stopTracker();
    const postData: ISendSmsDataProps = {
      patientGuid,
      topic,
      type: 'sms',
      message,
      isRpmPanel,
      isPrimaryNumber: contact === 'primary',
      timerDuration: isRpmPanel ? secondsRef.current : 0,
      trackerGuid: isRpmPanel ? trackerGuid : null,
    };
    const { error } = await submit(postData);
    if (error === 'close alert') {
      resumeTracker();
      return;
    }
    if (!error) {
      showResult();
      toggle();
      return;
    }
    if (isRpmPanel && isOpen) {
      startTracker();
    }
  };

  const onTemplateChange = (templateGuid: string) => {
    const smsTemplate = smsTemplates.find(
      (template) => templateGuid === template.id,
    );
    setTopic(smsTemplate?.topic ?? '');
    setMessage(smsTemplate?.text ?? '');
  };

  const rightTitle = getTrackerTitle({
    isMinimized,
    trackerTimer: time,
  });

  return (
    <>
      {isOpen && (
        <DraggableModal
          title={t('labels.sendSms')}
          rightTitle={isRpmPanel ? rightTitle : undefined}
          isOpen={isOpen}
          toggle={() => {
            toggle();
            deleteTracker();
          }}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          onSave={handleSubmit}
          isSaveDisabled={!message || createTrackerLoading}
          isSaveLoading={loading}
          saveLabel={t('controls.send')}
        >
          <div className={classNames(s['modal-content'], 'sms-popup-content')}>
            {isRpmPanel && (
              <FormGroup tag="fieldset">
                {smsTemplates.map((template) => (
                  <FormGroup check key={template.id}>
                    <Label check>
                      <Input
                        type="radio"
                        name="template_id"
                        value={template.id}
                        onChange={() => onTemplateChange(template.id)}
                      />
                      {template.topic}
                    </Label>
                  </FormGroup>
                ))}
              </FormGroup>
            )}
            <div>
              <TextArea
                className="sms-textarea"
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder={t('labels.typeOrSelectOption')}
              />
              <ContactTypeSelect
                patientGuid={patientGuid}
                onChange={setContact}
                value={contact}
              />
            </div>
          </div>
        </DraggableModal>
      )}
    </>
  );
};

export default SmsModal;
